/* SPEC-509 — renders a B mark (markData.ts): lines in currentColor at 1.5 strokes, the node
 * filled with `var(--mark-accent, currentColor)` — the SuryaMark pattern, so a surface sets
 * the node colour by setting `--mark-accent` on itself or an ancestor.
 *
 * Always decorative (aria-hidden, not focusable): every site names the control or heading in
 * text or in an aria-label beside it. */
import { DRAWER_MARKS, GROUP_MARKS, RAIL_MARKS, type DrawerMarkId, type GroupMarkId, type MarkDrawing, type MarkShape, type RailMarkId } from './markData';

const LINE = {
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.5,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
};
const NODE = {
  style: { fill: 'var(--mark-accent, currentColor)', stroke: 'var(--mark-accent, currentColor)' },
  strokeWidth: 0.6,
  strokeLinejoin: 'round' as const,
};

function shape(s: MarkShape, i: number, paint: object) {
  switch (s.el) {
    case 'path':
      return <path key={i} d={s.d} {...paint} />;
    case 'circle':
      return <circle key={i} cx={s.cx} cy={s.cy} r={s.r} {...paint} />;
    default:
      return <rect key={i} x={s.x} y={s.y} width={s.width} height={s.height} rx={s.rx} {...paint} />;
  }
}

export function MarkSvg({ drawing, size = 20, className }: { drawing: MarkDrawing; size?: number; className?: string }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      className={className}
      aria-hidden="true"
      focusable="false"
      style={{ overflow: 'visible', flexShrink: 0 }}
    >
      {drawing.lines.map((s, i) => shape(s, i, LINE))}
      {drawing.accent.map((s, i) => shape(s, drawing.lines.length + i, NODE))}
    </svg>
  );
}

export function GroupMark({ id, size = 16, className }: { id: GroupMarkId; size?: number; className?: string }) {
  return <MarkSvg drawing={GROUP_MARKS[id]} size={size} className={className} />;
}

export function DrawerMark({ id, size = 18, className }: { id: DrawerMarkId; size?: number; className?: string }) {
  return <MarkSvg drawing={DRAWER_MARKS[id]} size={size} className={className} />;
}

export function RailMark({ id, size = 20, className }: { id: RailMarkId; size?: number; className?: string }) {
  return <MarkSvg drawing={RAIL_MARKS[id]} size={size} className={className} />;
}
